import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { getFormValues, getFormMeta, getFormSyncWarnings, change } from "redux-form";
import { useSelector, useDispatch } from "react-redux";

import { Icon } from "rhombus-react";

import { getNewClientID, noop } from "utils/common";
import { MAX_CAROUSEL_CARDS_BY_ADTYPE } from "social/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import CardPreview from "./CardPreview";

import "./CardSelector.css";

function DropTarget(props){
	const { slot = 0, moveCard = noop } = props;
	const [canDrop, setCanDrop] = useState(false); /*
		Native drag and drop doesn't respect :hover selectors when you're dragging an element over them,
		so we have to manually apply another class (.can-drop) onDragEnter, and remove it onDragLeave/onDrop
	*/
	function allowDrop(e){
		if(e.dataTransfer.types.includes("order")){
			setCanDrop(true);
			e.preventDefault();
			e.stopPropagation();
		}
	}
	function handleDrop(e){
		e.preventDefault();
		e.stopPropagation(); // If you don't do this, Firefox leaves the app and opens images
		setCanDrop(false);
		const cardOrder = e.dataTransfer.getData("order");
		if(cardOrder){
			moveCard(Number(cardOrder), slot);
		}
	}
	return (
		<div
			className = {classNames({
				"drop-target": true,
				"can-drop": canDrop,
			})}
			onDragEnter = {allowDrop}
			onDragLeave = {() => { setCanDrop(false); }}
			onDragOver = {allowDrop}
			onDrop = {handleDrop}
		>
			<div className="drop-target-marker" />
		</div>
	);
}

export default function CardSelector(props){
	const dispatch = useDispatch();
	const {
		adType,
		libraryId,
		isViewOnly,
		showPristineErrors,
		thumbnailNameSelector, // This is forwarded to child CardPreview's.  See CardPreview for documentation.
	} = props;

	const [isDragging, setIsDragging] = useState(false);

	const { cards = [], currentCard = 0 } = useSelector((state) => {
		return getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {};
	});
	const cardWarnings = useSelector((state) => {
		return getFormSyncWarnings(SOCIAL_WIZARD)(state)?.[adType]?.cards || {};
	});
	const cardTouched = useSelector((state) => {
		const cardMeta = getFormMeta(SOCIAL_WIZARD)(state)?.[adType]?.cards || [];
		return cardMeta.map((card) => {
			return Object.values(card).some((field) => {
				return field.touched;
			});
		});
	});

	const canAddCard = !isViewOnly && (cards.length < MAX_CAROUSEL_CARDS_BY_ADTYPE[adType]);

	function selectCard(index){ // index is 0-based
		dispatch(change(SOCIAL_WIZARD, `${adType}.currentCard`, index));
	}

	function addCard(){
		if(!canAddCard){ return; }
		const newCardOrder = cards.length;
		dispatch(change(SOCIAL_WIZARD, `${adType}.cards`, [
			...cards,
			{
				type: "image",
				order: newCardOrder,
				clientId: getNewClientID(),
			}
		]));
		dispatch(change(SOCIAL_WIZARD, `${adType}.currentCard`, newCardOrder));
	}

	function moveCard(cardOrder, slot){
		/*
			Cards are set up to be interspersed between drop target slots.

			e.g.
			Slot 0 | Card 0 | Slot 1 | Card 1 | Slot 2 | Card 2 | Slot 3

			If the target slot is equal to, or 1 more than the cardOrder, the card doesn't move.

			Otherwise, if the card is moving right (slot > cardOrder), the card's new order is slot - 1
			If it's moving left (slot < cardOrder), the card's new order is slot.

			Cards between cardOrder and the new order will need to have their orders increased by 1 if the
			card is moving left, or decreased by 1 if the card is moving right.
		*/
		if((slot === cardOrder) || (slot === cardOrder + 1)){
			// Slot is adjacent to the card, so order doesn't change
			return;
		}

		const newOrder = (slot > cardOrder) ? slot - 1 : slot;
		const isMovingLeft = newOrder < cardOrder;
		const displacement = isMovingLeft ? 1 : -1;

		function isBetween(order){
			// Is `order` between the card that's moving's original order and its new order
			return (Math.min(cardOrder, newOrder) <= order) && (Math.max(cardOrder, newOrder) >= order);
		}

		const adjustedCards = cards.map((card) => {
			const { order } = card || {};

			if(order === cardOrder){
				// Replace the dropped card's order with its new order
				return {
					...card,
					order: newOrder,
				};
			}

			if(isBetween(order)){
				// Shift cards between the source and the new position
				return {
					...card,
					order: order + displacement,
				};
			}

			// Cards not dropped, and not between the source and drop position are unchanged
			return card;
		});

		dispatch(change(SOCIAL_WIZARD, `${adType}.cards`, adjustedCards));

		// The currentCard's identity should be maintained through reordering
		if(cardOrder === currentCard){
			// If the dropped card was the currentCard, set currentCard to its new order
			dispatch(change(SOCIAL_WIZARD, `${adType}.currentCard`, newOrder));
		} else if(isBetween(currentCard)){
			// If the currentCard was between the source and drop position, shift it by one
			dispatch(change(SOCIAL_WIZARD, `${adType}.currentCard`, currentCard + displacement));
		}
	}

	const cardList = cards
		.sort((cardA, cardB) => cardA.order - cardB.order)
		.map((card) => {
			const shouldShowError = showPristineErrors || cardTouched[card.order];
			return (
				<CardPreview
					key={card.clientId}
					onClick={() => { selectCard(card.order); }}
					draggable = {!isViewOnly}
					isSelected = {currentCard === card.order}
					hasError = {shouldShowError && !!cardWarnings[card.order]}
					card={card}
					libraryId={libraryId}
					thumbnailNameSelector={thumbnailNameSelector}
				/>
			);
		})
		.reduce((prev, card, index) => {
			if(index === 0){
				prev.push(<DropTarget key={`drop${0}`} slot={0} moveCard={moveCard} />);
			}
			prev.push(card, <DropTarget key={`drop${index + 1}`} slot={index + 1} moveCard={moveCard} />);
			return prev;
		}, [])
	;

	const addButton = (
		<div className="carousel-card-preview add-card-button" onClick={addCard}>
			<Icon name="add" />
		</div>
	);

	return (
		<div
			className={classNames({
				"carousel-card-selector": true,
				"is-dragging": isDragging,
			})}
			onDragStart={() => { setIsDragging(true); }}
			onDragEnd={() => { setIsDragging(false); }}
		>
			{cardList}
			{canAddCard && addButton}
		</div>
	);
}

CardSelector.displayName = "CardSelector";

CardSelector.propTypes = {
	adType: PropTypes.string,
	libraryId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	isViewOnly: PropTypes.bool,
	showPristineErrors: PropTypes.bool,
	thumbnailNameSelector: PropTypes.func,
};
